import React, { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Bell, X } from 'lucide-react'
import { notificationsApi } from '../../api/notifications'
import { useNotifStore } from '../../store/notifStore'
import { format } from 'date-fns'
import type { Notification } from '../../types'

const toastStackStyle: React.CSSProperties = {
  position: 'fixed',
  top: 68,
  right: 20,
  display: 'flex',
  flexDirection: 'column',
  gap: 12,
  zIndex: 1200,
  pointerEvents: 'none',
}

const toastCardStyle: React.CSSProperties = {
  width: 340,
  maxWidth: 'calc(100vw - 32px)',
  borderRadius: 14,
  background: '#ffffff',
  border: '1px solid #dbe3ee',
  boxShadow: '0 18px 44px rgba(15, 23, 42, 0.16)',
  overflow: 'hidden',
  pointerEvents: 'auto',
}

const toastButtonStyle: React.CSSProperties = {
  width: '100%',
  border: 'none',
  background: 'transparent',
  padding: '14px 16px',
  display: 'flex',
  alignItems: 'flex-start',
  gap: 12,
  textAlign: 'left',
  cursor: 'pointer',
}

const iconWrapStyle: React.CSSProperties = {
  width: 34,
  height: 34,
  borderRadius: 10,
  background: '#eef2ff',
  color: '#4f46e5',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
}

async function markReadQuietly(notification: Notification) {
  if (notification.isRead) return
  try {
    await notificationsApi.markRead(notification.id)
  } catch {
    // keep UI responsive even if mark-read fails
  }
}

export function NotificationToastHost() {
  const navigate = useNavigate()
  const { toasts, dismissToast, decrementUnread } = useNotifStore()

  useEffect(() => {
    if (toasts.length === 0) return

    const timers = toasts.map((toast) =>
      window.setTimeout(() => {
        dismissToast(toast.id)
      }, 6000)
    )

    return () => {
      timers.forEach((timer) => window.clearTimeout(timer))
    }
  }, [toasts, dismissToast])

  const openNotification = async (notification: Notification) => {
    dismissToast(notification.id)
    if (!notification.isRead) {
      decrementUnread()
      await markReadQuietly(notification)
    }
    navigate(`/issues/${notification.issue.id}`)
  }

  if (toasts.length === 0) return null

  return (
    <div style={toastStackStyle}>
      {toasts.map((notification) => (
        <div key={notification.id} style={toastCardStyle}>
          <button
            type="button"
            style={toastButtonStyle}
            onClick={() => void openNotification(notification)}
          >
            <div style={iconWrapStyle}>
              <Bell size={18} />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 12, fontWeight: 800, color: '#4f46e5', marginBottom: 4 }}>
                New assignment
              </div>
              <div style={{ fontSize: 14, fontWeight: 700, color: '#0f172a', marginBottom: 4 }}>
                {notification.issue.project?.name || 'Project'} · {notification.issue.identifier}
              </div>
              <div style={{ fontSize: 13, color: '#334155', marginBottom: 6 }}>
                {notification.issue.title}
              </div>
              <div style={{ fontSize: 12, color: '#64748b', display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                <span>Priority: {notification.issue.priority.replace(/_/g, ' ')}</span>
                <span>{format(new Date(notification.createdAt), 'MMM d, h:mm a')}</span>
              </div>
            </div>
            <div
              role="button"
              tabIndex={0}
              onClick={(event) => {
                event.stopPropagation()
                dismissToast(notification.id)
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault()
                  dismissToast(notification.id)
                }
              }}
              style={{ color: '#94a3b8', paddingTop: 2, flexShrink: 0 }}
              aria-label="Dismiss notification"
            >
              <X size={16} />
            </div>
          </button>
        </div>
      ))}
    </div>
  )
}
